import Header from '../components/Header'
import Footer from '../components/Footer'

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="card">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Disclaimer
          </h1>
          <p className="text-gray-400 text-sm mb-8">Last updated: February 5, 2026</p>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Important Notice</h3>
                <p className="text-gray-300">
                  ScamView provides fraud detection guidance as an assistive tool. Results are probabilistic and should not be considered
                  definitive legal or financial advice. Always exercise your own judgment and conduct independent verification.
                </p>
              </div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-3">1. Nature of Service</h2>
              <p>
                ScamView is an AI-powered fraud detection tool designed to assist job seekers in identifying potential
                recruitment scams. The service:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analyzes communications for common scam indicators</li>
                <li>Provides risk assessments based on pattern recognition</li>
                <li>Cross-references data with known scam databases</li>
              </ul>
              <p className="mt-2 font-semibold text-yellow-400">
                However, ScamView is NOT:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>A guarantee against fraud</li>
                <li>Legal or financial advice</li>
                <li>A background check service</li>
                <li>A verification of employment legitimacy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">2. Accuracy Limitations</h2>
              <p>
                While we strive for high accuracy, our service has limitations:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>False Positives:</strong> Legitimate opportunities may be flagged if they contain unusual patterns</li>
                <li><strong>False Negatives:</strong> Sophisticated scams may not be detected if they lack typical indicators</li>
                <li><strong>Evolving Tactics:</strong> Scammers constantly develop new methods that may not be in our database</li>
                <li><strong>Context Dependent:</strong> Insufficient information provided may lead to inaccurate assessments</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">3. User Responsibility</h2>
              <p>
                You are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Verifying information through independent research</li>
                <li>Making final decisions about engaging with recruiters</li>
                <li>Protecting your personal and financial information</li>
                <li>Reporting suspected fraud to appropriate authorities</li>
                <li>Any consequences arising from your actions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">4. No Liability for Outcomes</h2>
              <p>
                ScamView and its operators shall not be held liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Financial losses resulting from scams</li>
                <li>Missed opportunities due to false positive alerts</li>
                <li>Identity theft or data breaches from external parties</li>
                <li>Emotional distress or reputational harm</li>
                <li>Any direct, indirect, incidental, or consequential damages</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">5. Third-Party Information</h2>
              <p>
                Our service may include information from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Public databases and scam reporting platforms</li>
                <li>AI analysis tools (including OpenAI's GPT-4)</li>
                <li>DNS and domain registration services</li>
                <li>Community-submitted reports</li>
              </ul>
              <p className="mt-2">
                We do not guarantee the accuracy or completeness of third-party data and are not responsible
                for errors in external sources.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">6. Not Legal or Financial Advice</h2>
              <p className="font-semibold">
                ScamView does not provide legal, financial, or professional advice.
              </p>
              <p className="mt-2">
                Consult with appropriate professionals for:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Legal matters: Contact an attorney</li>
                <li>Financial concerns: Consult a financial advisor</li>
                <li>Identity theft: Report to FTC and local law enforcement</li>
                <li>Employment verification: Contact company directly through official channels</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">7. Service Availability</h2>
              <p>
                We do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Uninterrupted service availability</li>
                <li>Error-free operation</li>
                <li>Compatibility with all devices or browsers</li>
                <li>Timely updates to scam databases</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">8. External Links</h2>
              <p>
                Our service may contain links to third-party websites. We are not responsible for the
                content, privacy practices, or accuracy of external sites.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">9. Changes to Service</h2>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of the service at any
                time without notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-3">10. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless ScamView, its operators, and affiliates from any
                claims, damages, or expenses arising from your use of the service.
              </p>
            </section>

            <section className="pt-6 border-t border-white/10">
              <h2 className="text-2xl font-bold text-white mb-3">Best Practices</h2>
              <p className="mb-2">To protect yourself:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>✓ Never send money or financial information to unknown parties</li>
                <li>✓ Verify company information through official websites</li>
                <li>✓ Research job offers independently</li>
                <li>✓ Trust your instincts - if it seems too good to be true, it probably is</li>
                <li>✓ Use ScamView as ONE tool in your due diligence process</li>
                <li>✓ Report suspicious activity to authorities</li>
              </ul>
            </section>

            <section className="pt-6 border-t border-white/10">
              <p className="text-sm text-gray-500">
                By using ScamView, you acknowledge that you have read, understood, and agree to this disclaimer.
                If you do not agree, please discontinue use of the service immediately.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
